import cuid from "cuid";
import { getType } from "typesafe-actions";
import { IStore } from "../../store";
import { formSelectors } from "../../_core/form";
import { jobSelectors } from "../../_core/job";
import { IGarbageEditLog, IGRB2, IGRB2Store, TGarbageEntries } from "./@types";
import { EGRB2Actions } from "./actions";

const grb2Selectors = {
	_getStore(store: IStore) {
		return store.app.grb2;
	},
	getNewOperationState(store: IGRB2Store) {
		return store.newOperation.newOperationState;
	},
	getBasicFormData(store: IGRB2Store) {
		return store.newOperation.garbageBasicInfo;
	},
	getFormDataByEntry(entry: TGarbageEntries, store: IGRB2Store) {
		const {
			garbageAccidentalLoss,
			garbageDischargedIntoSea,
			garbageDischargedToReceptionAshoreOrOtherShips,
		} = store.newOperation;
		switch (entry) {
			case "GARBAGE_DISCHARGED_INTO_SEA":
				return garbageDischargedIntoSea;

			case "ACCIDENTAL_OR_LOSS_OF_GARBAGE_INTO_SEA":
				return garbageAccidentalLoss;

			case "GARBAGE_DISCHARGED_TO_RECEPTION_ASHORE_OR_OTHER_SHIPS":
				return garbageDischargedToReceptionAshoreOrOtherShips;
		}
	},
	getGRB2FromNewOperation(store: IGRB2Store) {
		const {
			garbageAccidentalLoss,
			garbageBasicInfo,
			garbageDischargedIntoSea,
			garbageDischargedToReceptionAshoreOrOtherShips,
		} = store.newOperation;
		switch (garbageBasicInfo.entry) {
			case "GARBAGE_DISCHARGED_INTO_SEA":
				return {
					...garbageBasicInfo,
					...garbageDischargedIntoSea,
				};
			case "ACCIDENTAL_OR_LOSS_OF_GARBAGE_INTO_SEA":
				return {
					...garbageBasicInfo,
					...garbageAccidentalLoss,
				};
			case "GARBAGE_DISCHARGED_TO_RECEPTION_ASHORE_OR_OTHER_SHIPS":
				return {
					...garbageBasicInfo,
					...garbageDischargedToReceptionAshoreOrOtherShips,
				};
		}
	},
	getHistoryList(store: IGRB2Store) {
		return store.records.ids.map((id) => ({
			...store.records.byIds[id],
			editHistory: grb2Selectors.getGRB2EditHistory(id, store),
		}));
	},
	getGRB2Ids(store: IGRB2Store) {
		return store.records.ids;
	},
	getGRB2ById(grb2Id: string, store: IGRB2Store) {
		return store.records.byIds[grb2Id];
	},
	getGRB2FromHistory(grb2Id: string, store: IGRB2Store) {
		return store.records.byIds[grb2Id];
	},
	getGRB2EditHistory(grb2Id: string, store: IGRB2Store) {
		const grb2 = grb2Selectors.getGRB2FromHistory(grb2Id, store);
		const uniqueFields = Object.keys(grb2.editLogs)
			.reduce<{ fieldName: string; fieldLabel: string }[]>(
				(result, editSessionNumber) => {
					const editedItems = grb2.editLogs[parseInt(editSessionNumber, 10)];
					return [
						...result,
						...editedItems.ids.map((itemId) => {
							return {
								fieldName: editedItems.byIds[itemId].fieldName,
								fieldLabel: editedItems.byIds[itemId].fieldLabel,
							};
						}),
					];
				},
				[]
			)
			.filter(
				(item, i, ar) =>
					ar.findIndex(
						(indexItem) => indexItem.fieldName === item.fieldName
					) === i
			);

		const editHistory = uniqueFields.map((field) => {
			return {
				...field,
				edits: Object.keys(grb2.editLogs).reduce<IGarbageEditLog[]>(
					(result, editSessionNumber) => {
						const editedItemsByIds =
							grb2.editLogs[parseInt(editSessionNumber, 10)].byIds;
						if (editedItemsByIds[field.fieldName])
							return [...result, editedItemsByIds[field.fieldName]];
						return result;
					},
					[]
				),
			};
		});
		return editHistory;
	},
	getEditHistoryForGarbage(grb2: IGRB2) {
		const uniqueFields = Object.keys(grb2.editLogs)
			.reduce<{ fieldName: string; fieldLabel: string }[]>(
				(result, editSessionNumber) => {
					const editedItems = grb2.editLogs[parseInt(editSessionNumber, 10)];
					return [
						...result,
						...editedItems.ids.map((itemId) => {
							return {
								fieldName: editedItems.byIds[itemId].fieldName,
								fieldLabel: editedItems.byIds[itemId].fieldLabel,
							};
						}),
					];
				},
				[]
			)
			.filter(
				(item, i, ar) =>
					ar.findIndex(
						(indexItem) => indexItem.fieldName === item.fieldName
					) === i
			);

		const editHistory = uniqueFields.map((field) => {
			return {
				...field,
				edits: Object.keys(grb2.editLogs).reduce<IGarbageEditLog[]>(
					(result, editSessionNumber) => {
						const editedItemsByIds =
							grb2.editLogs[parseInt(editSessionNumber, 10)].byIds;
						if (editedItemsByIds[field.fieldName])
							return [...result, editedItemsByIds[field.fieldName]];
						return result;
					},
					[]
				),
			};
		});
		return editHistory;
	},
	getFilter(store: IGRB2Store) {
		return {
			...store.filter,
			shownRecords: store.filter?.shownRecords
				? store.filter?.shownRecords
				: "ALL",
		};
	},
	jobToLoadHistory(store: IGRB2Store) {
		return jobSelectors.getJobStatus(store, EGRB2Actions.HISTORY_LOAD);
	},
	jobToProcessAttachment(activeTaskStore: IGRB2Store) {
		return jobSelectors.getJobStatus(
			activeTaskStore,
			EGRB2Actions.OPERATION_ATTACHMENT_UPLOAD
		);
	},
};

export default grb2Selectors;
